import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faExclamationTriangle,
  faPencilAlt,
  faBan,
  faEye,
} from '@fortawesome/free-solid-svg-icons';

const LoadIssuesModal = ({
  isOpen,
  onClose,
  loadId,
  load,
  onEditClick,
  onDetailsClick,
  onCancelDriverClick,
}) => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadData, setLoadData] = useState(null);

  // Get the actual load ID from either prop
  const actualLoadId = loadId || (load && load.id);

  useEffect(() => {
    const fetchLoad = async () => {
      try {
        const { data, error } = await supabase
          .from('loads')
          .select('*')
          .eq('id', actualLoadId)
          .single();

        if (error) throw error;
        setLoadData(data);
      } catch (error) {
        console.error('Error fetching load:', error);
      }
    };

    const fetchIssues = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('load_issues')
          .select('*')
          .eq('load_id', actualLoadId)
          .order('created_at', { ascending: false });

        if (error) throw error;
        setIssues(data || []);
      } catch (error) {
        console.error('Error fetching issues:', error);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen && actualLoadId) {
      fetchIssues();
      if (load) {
        setLoadData(load);
      } else if (loadId) {
        fetchLoad();
      }
    } else if (!isOpen) {
      // Reset state when modal closes
      setIssues([]);
      setLoadData(null);
      setLoading(true);
    }
  }, [isOpen, actualLoadId, load, loadId]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 1050 }}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-xl font-semibold text-gray-800 flex items-center">
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              className="text-red-500 mr-2"
            />
            Yük Sorunları
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 focus:outline-none"
          >
            <FontAwesomeIcon icon={faTimes} className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center items-center h-40">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
            </div>
          ) : issues.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Bu yük için kayıtlı sorun bulunamadı.
            </div>
          ) : (
            <div className="space-y-6">
              {issues.map((issue) => (
                <div
                  key={issue.id}
                  className="border rounded-lg p-4 bg-gray-50"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="font-medium text-lg text-red-600">
                      {issue.issue.report_type === 'revision'
                        ? 'Yükleme Planı Güncelleme'
                        : 'Taşıma İptal Talebi'}
                    </div>
                    <div className="text-sm text-gray-500">
                      {formatDate(issue.created_at)}
                    </div>
                  </div>
                  <div className="text-gray-700 mb-3 whitespace-pre-wrap">
                    {issue.issue.description}
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <div className="text-gray-600">
                      <span className="font-medium">Durum:</span>{' '}
                      <span
                        className={`${issue.status === 'resolved' ? 'text-green-600' : 'text-orange-600'}`}
                      >
                        {issue.status === 'resolved' ? 'Çözüldü' : 'Beklemede'}
                      </span>
                    </div>
                  </div>
                  {issue.resolution && issue.status === 'resolved' && (
                    <div className="mt-3 pt-3 border-t">
                      <div className="font-medium text-green-600 mb-1">
                        Çözüm:
                      </div>
                      <div className="text-gray-700 whitespace-pre-wrap">
                        {issue.resolution}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border-t px-6 py-4 flex justify-end space-x-3">
          {onEditClick && (
            <button
              onClick={() => {
                onEditClick(loadData || load)
                onClose()
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
            >
              <FontAwesomeIcon icon={faPencilAlt} className="mr-2" />
              Düzenle
            </button>
          )}
          {(
            <button
              onClick={() => {
                onDetailsClick(loadData || load)
                onClose()
              }}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center"
            >
              <FontAwesomeIcon icon={faEye} className="mr-2" />
              Yük Detayları
            </button>
          )}
          {onCancelDriverClick && (
            <button
              onClick={() => onCancelDriverClick(actualLoadId)}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center"
            >
              <FontAwesomeIcon icon={faBan} className="mr-2" />
              Sürücüyü İptal et
            </button>
          )}
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoadIssuesModal;